import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import useUserPermissions from '../../../hooks/useUserPermissions';
import gridApi from '../../api/gridApi';
import { formatCurrency, formatPercent } from '../../../utils/formatters';
import StatCard from '../../common/StatCard/StatCard';
import UserSelector from '../../common/UserSelector';
import TradeHistory from './TradeHistory';
import { showError } from '../../../utils/sweetAlertHelper';
import './GridDashboard.css';

function GridDashboard() {
  const { user } = useContext(AuthContext);
  const { isAdmin } = useUserPermissions();
  
  const [targetUserId, setTargetUserId] = useState(user?._id);
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [botStatus, setBotStatus] = useState(null);
  const [gridState, setGridState] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    if (user?._id && !targetUserId) setTargetUserId(user._id);
  }, [user]);

  const fetchAllData = useCallback(async (isAuto = false) => {
    if (!targetUserId) return;
    if (!isAuto) setLoading(true);
    
    try {
      const ts = Date.now();
      
      if (!isAuto) {
        try {
          await gridApi.syncOrders(symbol);
        } catch (syncError) {
          console.warn('Sync failed:', syncError);
        }
      }
      
      const [statusRes, stateRes, statsRes, ordersRes] = await Promise.allSettled([
        gridApi.getBotStatus(targetUserId, symbol, ts),
        gridApi.getGridState(targetUserId, symbol, ts),
        gridApi.getGridStats(targetUserId, symbol, ts),
        gridApi.getGridOrders(targetUserId, symbol, 'OPEN', ts)
      ]);

      if (statusRes.status === 'fulfilled') setBotStatus(statusRes.value.data);
      if (stateRes.status === 'fulfilled') setGridState(stateRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.data);

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (!isAuto) setLoading(false);
    }
  }, [targetUserId, symbol]);

  useEffect(() => {
    fetchAllData(false);
  }, [fetchAllData]);

  useEffect(() => {
    const interval = setInterval(() => fetchAllData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  const handleRefresh = () => fetchAllData(false);
  const handleStart = async () => {
    try {
      await gridApi.startBot(targetUserId, symbol);
      await fetchAllData(false);
    } catch (err) {
      await showError(err.message, 'Error al Iniciar Bot');
    }
  };

  const handleStop = async() => {
    try {
      await gridApi.stopBot(targetUserId, symbol);
      await fetchAllData(false);
    } catch (err) {
      await showError(err.message, 'Error al Detener Bot');
    }
  };

  const isRunning = botStatus?.isRunning || false;
  const currentState = botStatus?.state || gridState?.state || null;
  const currentStats = stats?.stats || null;
  const buyOrders = orders?.orders?.filter(o => o.side === 'BUY') || [];
  const sellOrders = orders?.orders?.filter(o => o.side === 'SELL') || [];
  const baseAsset = symbol.replace('USDT', '');

  // Calcular precio promedio ponderado de Buy orders
  const avgBuyPrice = buyOrders.length > 0
    ? buyOrders.reduce((sum, o) => sum + (o.price * o.quantity), 0) /
      buyOrders.reduce((sum, o) => sum + o.quantity, 0)
    : 0;

  // Calcular precio promedio ponderado de Sell orders
  const avgSellPrice = sellOrders.length > 0
    ? sellOrders.reduce((sum, o) => sum + (o.price * o.quantity), 0) /
      sellOrders.reduce((sum, o) => sum + o.quantity, 0)
    : 0;

  // Valor total de las sell orders (suma de cantidad × precio de cada orden)
  const btcValueAtSellPrice = sellOrders.reduce((sum, o) => sum + (o.price * o.quantity), 0);

  return (
    <div className="grid-dashboard-pro">
      {/* Page Header */}
      <div className="page-header-pro">
        <div className="header-left">
          <h1 className="page-title-pro">📊 Grid Trading Bot</h1>
          <p className="page-subtitle-pro">Automated grid strategy for {symbol}</p>
        </div>
        <div className="header-right">
          {isAdmin && (
            <UserSelector
              currentUser={user}
              selectedUserId={targetUserId}
              onUserChange={setTargetUserId}
            />
          )}
          <button className="btn btn-secondary" onClick={handleRefresh} disabled={loading}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar-pro">
        <div className="status-item">
          <span className={`status-dot ${isRunning ? 'running' : 'stopped'}`}></span>
          <span className="status-text">{isRunning ? 'Bot Running' : 'Bot Stopped'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Symbol:</span>
          <span className="status-value">{symbol}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Price:</span>
          <span className="status-value">{formatCurrency(currentState?.currentPrice)}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Last Update:</span>
          <span className="status-value">{lastRefresh.toLocaleTimeString()}</span>
        </div>
        <div className="status-actions">
          {!isRunning ? (
            <button className="btn btn-success btn-sm" onClick={handleStart}>▶ Start Bot</button>
          ) : (
            <button className="btn btn-danger btn-sm" onClick={handleStop}>⏹ Stop Bot</button>
          )}
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="stats-row-pro">
        <StatCard
          title="Total Profit"
          value={formatCurrency(currentStats?.totalProfit || 0)}
          change={formatPercent(currentStats?.profitPercent || 0)}
          trend={currentStats?.totalProfit >= 0 ? 'up' : 'down'}
          icon="💰"
        />
        <StatCard
          title="Total Equity"
          value={formatCurrency(currentState?.equity || 0)}
          icon="💼"
        />
        <StatCard
          title="Active Orders"
          value={(buyOrders.length + sellOrders.length).toString()}
          icon="📋"
        />
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid-pro">
        {/* Left Column */}
        <div className="grid-col-pro">
          {/* Wallet Balance */}
          <div className="card-pro">
            <div className="card-header-pro">
              <h3>💼 Wallet Balance</h3>
            </div>
            <div className="card-body-pro">
              <div className="balance-item">
                <div className="balance-info">
                  <span className="balance-label">₿ {baseAsset}</span>
                  <span className="balance-amount">{(currentState?.balanceBase || 0).toFixed(8)}</span>
                </div>
                <div className="balance-value">{formatCurrency((currentState?.balanceBase || 0) * (currentState?.currentPrice || 0))}</div>
              </div>
              <div className="balance-bar">
                <div className="balance-fill balance-base" style={{ width: `${currentState?.baseRatio || 0}%` }}></div>
              </div>
              
              <div className="balance-item mt-4">
                <div className="balance-info">
                  <span className="balance-label">💵 USDT</span>
                  <span className="balance-amount">{(currentState?.balanceQuote || 0).toFixed(2)}</span>
                </div>
                <div className="balance-value">{formatCurrency(currentState?.balanceQuote || 0)}</div>
              </div>
              <div className="balance-bar">
                <div className="balance-fill balance-quote" style={{ width: `${currentState?.quoteRatio || 0}%` }}></div>
              </div>
            </div>
          </div>

          {/* Grid Configuration */}
          <div className="card-pro">
            <div className="card-header-pro">
              <h3>⚙️ Grid Config</h3>
            </div>
            <div className="card-body-pro">
              <div className="config-row">
                <span>Upper Bound:</span>
                <strong>{formatCurrency(currentState?.upperBound)}</strong>
              </div>
              <div className="config-row">
                <span>Mid Price:</span>
                <strong>{formatCurrency(currentState?.midPrice)}</strong>
              </div>
              <div className="config-row">
                <span>Lower Bound:</span>
                <strong>{formatCurrency(currentState?.lowerBound)}</strong>
              </div>
            </div>
          </div>

          {/* Order Prices & Valuations */}
          <div className="card-pro">
            <div className="card-header-pro">
              <h3>📊 Order Prices</h3>
            </div>
            <div className="card-body-pro">
              <div className="config-row">
                <span>🟢 Avg Buy Price:</span>
                <strong>{formatCurrency(avgBuyPrice)}</strong>
              </div>
              <div className="config-row">
                <span>🔴 Avg Sell Price:</span>
                <strong>{formatCurrency(avgSellPrice)}</strong>
              </div>
              <div className="config-row" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <span>₿ Value (Current):</span>
                <strong>{formatCurrency((currentState?.balanceBase || 0) * (currentState?.currentPrice || 0))}</strong>
              </div>
              <div className="config-row">
                <span>₿ Value (Sell Orders):</span>
                <strong style={{ color: avgSellPrice > (currentState?.currentPrice || 0) ? '#4caf50' : '#ff5252' }}>
                  {formatCurrency(currentState?.btcValueSellOrders || btcValueAtSellPrice)}
                </strong>
              </div>
              <div className="config-row">
                <span>₿ Sell Orders + USDT:</span>
                <strong style={{ color: avgSellPrice > (currentState?.currentPrice || 0) ? '#4caf50' : '#ff5252' }}>
                  {formatCurrency(
                    currentState?.sellOrdersPlusUSDT
                    || (btcValueAtSellPrice + (currentState?.balanceQuote || 0))
                  )}
                </strong>
              </div>
              {currentState?.orphanBTC > 0 && (
                <div className="config-row" style={{ fontSize: '0.8em', opacity: 0.7 }}>
                  <span>↳ includes {currentState.orphanBTC.toFixed(5)} BTC unassigned</span>
                  <span>{formatCurrency(currentState.orphanValue)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Center Column - Grid Visualization */}
        <div className="grid-col-pro grid-col-center">
          <div className="card-pro card-grid-visual">
            <div className="card-header-pro">
              <h3>📈 Grid Orders</h3>
              <div className="grid-legend">
                <span className="legend-item"><span className="dot dot-sell"></span> Sell Orders ({sellOrders.length})</span>
                <span className="legend-item"><span className="dot dot-buy"></span> Buy Orders ({buyOrders.length})</span>
              </div>
            </div>
            <div className="card-body-pro">
              {/* Current Price Indicator */}
              <div className="current-price-indicator">
                <span className="price-dot"></span>
                <span className="price-text">Current Price: {formatCurrency(currentState?.currentPrice)}</span>
              </div>

              {/* Grid Orders Display */}
              <div className="orders-grid-display">
                {/* Sell Orders */}
                <div className="orders-section sell-section">
                  <div className="section-label">🔴 SELL ORDERS</div>
                  <div className="orders-list-pro">
                    {sellOrders.map((order, idx) => (
                      <div key={order._id || idx} className="order-item-pro sell-order">
                        <span className="order-price">{formatCurrency(order.price)}</span>
                        <span className="order-qty">{order.quantity?.toFixed(6)} {baseAsset}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Buy Orders */}
                <div className="orders-section buy-section">
                  <div className="section-label">🟢 BUY ORDERS</div>
                  <div className="orders-list-pro">
                    {buyOrders.sort((a, b) => b.price - a.price).map((order, idx) => (
                      <div key={order._id || idx} className="order-item-pro buy-order">
                        <span className="order-price">{formatCurrency(order.price)}</span>
                        <span className="order-qty">{order.quantity?.toFixed(6)} {baseAsset}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Trade History - Full Width */}
      <div className="card-pro card-pro-large">
        <div className="card-header-pro">
          <h3>📜 Trade History</h3>
        </div>
        <div className="card-body-pro" style={{ padding: 0 }}>
          <TradeHistory symbol={symbol} userId={targetUserId} />
        </div>
      </div>
    </div>
  );
}

export default GridDashboard;
